import { RootState } from "@/models/RootState.type";
import { AppDispatch } from "@/models/AppDispatch.type";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import {
  aggregateLessonsByLessonPlanIds,
  batchCreateStudentAssignments,
  batchUnassignAssignments,
  batchUpdateAssignments,
  createAssignment,
  updateCRUDAssignmentsQueue,
  WeeklyPlanState,
} from "../slices";
import {
  Assignment,
  AssignmentCRUDCategory,
  BatchAddAssignmentInput,
  BatchUpdateAssignmentInput,
  CreateAssignmentInput,
} from "@/models";
import { Entity } from "@/enums";
import { getRosterInfo } from "@/hooks/useRosterInfo";

// Parse methods are here to catch new parent ids from queue updates
const findAssignmentByLessonPlanId = (
  lessonPlanId: string,
  type: Entity[],
  existingAssignments: Assignment[]
) => {
  // we are expecting to be able to find lesson assignments by a unique lesson plan id for assignments gathered
  for (let i = 0; i < type.length; i++) {
    const t = type[i];
    const found = existingAssignments.find(
      (ea) => t === ea.entityType && ea.lessonPlanId === lessonPlanId
    );
    if (found) {
      return found;
    }
  }
  return null;
};
const getParentEntityList = (entity: string) => {
  // return array order matters go lowest level first to highest
  switch (entity) {
    case Entity.STUDENT:
      return [Entity.CLASS, Entity.SCHOOL, Entity.DISTRICT];
    case Entity.CLASS: 
      return [Entity.SCHOOL, Entity.DISTRICT];
    case Entity.SCHOOL:
      return [Entity.DISTRICT];
    default:
      return [];
  }
}
const parseBatchCreateInput = (
  input: BatchAddAssignmentInput,
  existingAssignments: Assignment[]
) => {
  const newInput = structuredClone(input);
  const entityList = getParentEntityList(newInput.entityType);

  const parentAssign = findAssignmentByLessonPlanId(
    newInput.lessonPlanId,
    entityList,
    existingAssignments
  );
  // parent assignment id could have changed so if so update it as well
  if (parentAssign && parentAssign.assignmentId !== "-1") {
    newInput.parentId = parentAssign.assignmentId;
  }

  return newInput;
};
const parseCreateAssignInput = (
  input: CreateAssignmentInput,
  existingAssignments: Assignment[]
) => {
  const newInput = structuredClone(input);
  const entityList = getParentEntityList(newInput.entityType);

  const parentAssign = findAssignmentByLessonPlanId(
    newInput.lessonPlanId,
    entityList,
    existingAssignments
  );
  // parent assignment id could have changed so if so update it as well
  if (parentAssign && parentAssign.assignmentId !== "-1") {
    newInput.parentId = parentAssign.assignmentId;
  }

  return newInput;
};
const parseBatchUpdateInput = (
  lessonPlanId: string,
  input: BatchUpdateAssignmentInput[],
  existingAssignments: Assignment[]
) => {
  const newInput = structuredClone(input);
  newInput.forEach((i) => {
    const entityList = getParentEntityList(i.entityType);

    const parentAssign = findAssignmentByLessonPlanId(
      lessonPlanId,
      entityList,
      existingAssignments
    );
    // parent assignment id could have changed so if so update it as well
    if (parentAssign && parentAssign.assignmentId !== "-1") {
      i.parentId = parentAssign.assignmentId;
    }
  });

  return newInput;
};

export const shiftQueueAssignmentExec = (
  listenerApi: any,
  weeklyPlan: WeeklyPlanState,
  reports: any,
  userData: any
) => {
  if (
    weeklyPlan.data?.crudAssignmentQueue &&
    weeklyPlan.data?.crudAssignmentQueue.length
  ) {
    const { crudAssignmentQueue } = weeklyPlan.data;
    let newCrudAssignmentQueue = [...crudAssignmentQueue];
    const crudAssignment = newCrudAssignmentQueue.shift();

    switch (crudAssignment!.crudType) {
      case AssignmentCRUDCategory.BatchCreateAssignments:
        listenerApi.dispatch(
          batchCreateStudentAssignments({
            input: parseBatchCreateInput(
              crudAssignment?.assignmentInput as BatchAddAssignmentInput,
              weeklyPlan.data?.assignments ?? []
            ),
          })
        );
        break;
      case AssignmentCRUDCategory.BatchUnassignAssignments:
        listenerApi.dispatch(
          batchUnassignAssignments({
            assignments:
              crudAssignment?.assignmentInput as BatchUpdateAssignmentInput[],
          })
        );
        break;
      case AssignmentCRUDCategory.BatchUpdateAssignments:
        listenerApi.dispatch(
          batchUpdateAssignments({
            assignments: parseBatchUpdateInput(
              crudAssignment?.lessonPlanId ?? "",
              crudAssignment?.assignmentInput as BatchUpdateAssignmentInput[],
              weeklyPlan.data?.assignments ?? []
            ),
          })
        );
        break;
      case AssignmentCRUDCategory.CreateAssignment:
        listenerApi.dispatch(
          createAssignment({
            assignment: parseCreateAssignInput(
              crudAssignment?.assignmentInput as CreateAssignmentInput,
              weeklyPlan.data?.assignments ?? []
            ),
          })
        );
        break;
      default:
        break;
    }

    listenerApi.dispatch(updateCRUDAssignmentsQueue(newCrudAssignmentQueue));
  } else if (
    weeklyPlan.data?.crudResponseByLessonPlanId &&
    weeklyPlan.data?.crudResponseByLessonPlanId.length
  ) {
    const { students } = getRosterInfo(reports, userData);
    // TODO Refetch Mastery here? or possibly do only on detail page viewing?
    listenerApi.dispatch(
      aggregateLessonsByLessonPlanIds({
        lessonPlanIds: weeklyPlan.data.crudResponseByLessonPlanId,
        studentCount: students.length,
      })
    );
  }
};

const effect = async (action: any, listenerApi: any) => {
  try {
    const { weeklyPlan, reports, userData } =
      listenerApi.getState() as RootState;
    shiftQueueAssignmentExec(listenerApi, weeklyPlan, reports, userData);
  } catch (error) {
    console.error("Error crud assignments:", error);
    // TODO dispatch a fetch retry action maybe?
    // listenerApi.dispatch(setLoading(AsyncLoadingState.ERROR));
  }
};

export const addBatchCreateStudentAssignmentsListenerMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: batchCreateStudentAssignments.fulfilled,
    effect,
  });
};

export const addBatchUnassignAssignmentsListenerMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: batchUnassignAssignments.fulfilled,
    effect,
  });
};

export const addBatchUpdateAssignmentsListenerMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: batchUpdateAssignments.fulfilled,
    effect,
  });
};

export const addCreateAssignmentListenerMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: createAssignment.fulfilled,
    effect,
  });
};
